import {
  ContentfulProductGrid,
  ContentfulProductSource,
  ContentfulProductGridMobileLayout,
} from '@jbhi-fi/apis/src/contentful/types';
import { ProductGridProps } from '../../ProductGrid/ProductGrid';
import { TrackingDescriptorType } from '../../Tracking/types';
import React from 'react';
import { UseProductsProps } from '../../hooks/useProducts/useProducts';
import { CollectionTransformer } from './CollectionTransformer';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { AlgoliaHitTransformer } from '../../Algolia/transformers/AlgoliaHitTransformer';
import { ProductCard } from '../../ProductCard';
import { useProductCompare } from '../../ProductCompare';
import { useCustomerContext } from '../../context/CustomerContextProvider';
import { useProductActionsContext } from '../../context/ProductActionsProvider';
import { ProductCardProps } from '../../ProductCard/types';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

export abstract class ProductGridTransformer {
  static toProductGridProps = (
    entry: ContentfulProductGrid,
    options: {
      productTrackingData?: { descriptor?: TrackingDescriptorType; listPrefix?: string; trackingListName?: string };
      productCardVariant?: ProductCardProps['variant'];
    } = {}
  ): ProductGridProps => {
    const { fields } = entry;
    const trackingListName =
      options.productTrackingData?.trackingListName ||
      (fields?.heading ? `${options.productTrackingData?.listPrefix ?? ''}${fields.heading}` : undefined);
    return {
      heading: fields?.heading,
      trackingListName,
      shouldTrackImpressions: true,
      productCardVariant: options?.productCardVariant,
      mobileLayout: fields?.mobileLayout,
      desktopRows: fields?.desktopRows ?? 2,
      ...ProductGridTransformer.toProductSourceProps(entry),
      renderProduct: ({ hit, position }) => (
        <ProductGridTransformer.ProductComponent
          hit={hit}
          position={position}
          trackingDescriptor={options?.productTrackingData?.descriptor}
          trackingListPrefix={options?.productTrackingData?.listPrefix}
          trackingListName={trackingListName || 'STACK - Product Grid'}
          variant={options?.productCardVariant}
          mobileLayout={fields?.mobileLayout}
        />
      ),
    };
  };

  static toProductSourceProps = (entry: ContentfulProductGrid): Partial<UseProductsProps> => {
    switch (entry.fields?.productSource) {
      case ContentfulProductSource.Algolia:
        return {
          algoliaQuery: entry.fields?.algoliaFilterQuery,
        };
      case ContentfulProductSource.Products:
        return {
          productVariantIds: entry.fields?.products ?? [],
        };
      case ContentfulProductSource.Collection:
        return entry.fields?.collection ? CollectionTransformer.toProductSourceProps(entry.fields.collection) : {};
      default:
        return {
          algoliaQuery: entry.fields?.algoliaFilterQuery,
          productVariantIds: entry.fields?.products,
        };
    }
  };

  static ProductComponent = ({
    hit,
    position,
    trackingDescriptor: trackingDescriptorProp,
    trackingListName: trackingListNameProp,
    trackingListPrefix,
    variant = 'tablet',
    mobileLayout = ContentfulProductGridMobileLayout.Stack,
  }: {
    hit: AlgoliaProductHit;
    position?: number;
    trackingDescriptor?: TrackingDescriptorType;
    trackingListName?: string;
    trackingListPrefix?: string;
    variant?: ProductCardProps['variant'];
    mobileLayout?: ContentfulProductGridMobileLayout;
  }) => {
    const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
    const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
    const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
    const isMobile = useBreakpoint('sm', 'down');
    let trackingListName =
      trackingListNameProp || trackingDescriptorProp?.ecommerceExtraProps?.list || 'STACK - Product Grid';
    trackingListName &&= trackingListPrefix ?? '';
    const trackingDescriptor: TrackingDescriptorType = {
      page: '',
      group: trackingListName,
      category: trackingListName,
      ...(trackingDescriptorProp ?? {}),
      ecommerceExtraProps: {
        ...(trackingDescriptorProp?.ecommerceExtraProps ?? {}),
        position,
        list: trackingListName,
      },
    };
    const productVariant = mobileLayout === ContentfulProductGridMobileLayout.Deck && isMobile ? 'deck' : variant;
    const isDynamicWidth = productVariant !== 'deck';

    return (
      <ProductCard
        {...AlgoliaHitTransformer.toProductCardProps(hit, { trackingDescriptor })}
        {...productCartPropsOverrides}
        variant={productVariant}
        isDynamicWidth={isDynamicWidth}
        getIsCompared={isAddedToCompare}
        onCompare={compareHandleClick}
        getShouldShowCompare={shouldShowCompare}
        isLoggedIn={isLoggedIn}
        onUpdateWishlist={handleUpdateWishlist}
        getIsInWishlist={getIsInWishlist}
        onAddToCart={
          onAddToCart
            ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
            : undefined
        }
      />
    );
  };
}
